import Icon from '@ui/icons/icon';
import styles from './video-player.module.css';
import { ICONS } from '@ui/icons/types';
import React, { useCallback, useState, type SyntheticEvent, useEffect, type CSSProperties, useRef } from 'react';
import { formatMsToHMMSS } from '@/utils/dates';
import classNames from 'classnames';

interface Props {
  videoUrl: string;
  thumbnailUrl?: string;
  nextVideo: () => void;
  lastVideo: () => void;
  canSelectNext: boolean;
  canSelectLast: boolean;
  isLower?: boolean;
}

const dragInit = {
  left: 0,
  x: 0,
  width: 0,
  mouseLeftCorrect: 0,
  wasPlaying: false,
};

export default function VideoPlayer({
  videoUrl,
  thumbnailUrl,
  nextVideo,
  lastVideo,
  canSelectLast,
  canSelectNext,
  isLower,
}: Props) {
  const [secondsPlayed, setSecondsPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [pause, setPause] = useState(true);
  const [canPlay, setCanPlay] = useState(false);
  const [mute, setMute] = useState(false);
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState('0%');
  const [url, setUrl] = useState<string | undefined>();
  const [thumbnail, setThumbnail] = useState(thumbnailUrl);

  const tickRef = useRef<NodeJS.Timeout | null>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const dragData = useRef<{ left: number; x: number; width: number; mouseLeftCorrect: number; wasPlaying: boolean }>(
    dragInit
  );

  const onMouseDownThumb = useCallback(
    (e: React.PointerEvent) => {
      // user began dragging the thumb grabber on the track
      e.stopPropagation();
      draggingRef.current = true;
      setDragging(true);
      if (trackRef.current) {
        // get the current offset of the track from the left-edge of the window
        const rect = trackRef.current.getBoundingClientRect();
        dragData.current.left = rect.left;
        dragData.current.width = rect.width;
        dragData.current.x = e.clientX;
        dragData.current.mouseLeftCorrect = 0;
        dragData.current.wasPlaying = !pause;
        setPause(true);
      }
    },
    [pause]
  );

  const onMouseDownTrack = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();

      if (trackRef.current && video && parentRef.current && thumbRef.current) {
        // get the current offset of the track element
        const rect = trackRef.current.getBoundingClientRect();

        // get the current position of the thumb dragger
        const thumbRect = thumbRef.current.getBoundingClientRect();

        // jump the current time to the percentage left of the width of the track slider immediately
        const pxLeft = e.clientX - rect.left;
        const percLeft = pxLeft / rect.width;
        video.currentTime = video.duration * percLeft;
        setSecondsPlayed(video.currentTime);

        // jump the thumb grabber to the current cursor position
        const thumbCenterOffset = thumbRect.width / 2;
        const mouseDelta = e.clientX - thumbRect.x - thumbCenterOffset;
        dragData.current.mouseLeftCorrect = mouseDelta;
        dragData.current.left = rect.left;
        dragData.current.width = rect.width;
        dragData.current.x = e.clientX;
        parentRef.current.style.setProperty('--thumb-progress', `${mouseDelta}px`);

        // begin dragging as if the user started dragging the thumb grabber
        draggingRef.current = true;
        setDragging(true);

        // pause the video but allow it to auto-resume if it was previously playing
        dragData.current.wasPlaying = !pause;
        setPause(true);
      }
    },
    [video, pause]
  );

  const onMouseMove = useCallback(
    (e: PointerEvent) => {
      if (draggingRef.current && trackRef.current && parentRef.current && video) {
        // set the current play time to the percentage left within the track
        const delta = e.clientX - dragData.current.left;
        const mouseDelta = e.clientX - dragData.current.x + dragData.current.mouseLeftCorrect;
        video.currentTime = video.duration * (delta / dragData.current.width);
        parentRef.current.style.setProperty('--thumb-progress', `${mouseDelta}px`);
      }
    },
    [video]
  );

  const onMouseUp = useCallback(
    (e: PointerEvent) => {
      if (draggingRef.current && parentRef.current && video) {
        // set the current play time to the percentage left within the track
        const delta = e.clientX - dragData.current.left;
        video.currentTime = video.duration * (delta / dragData.current.width);
        draggingRef.current = false;
        parentRef.current.style.setProperty('--thumb-progress', `0px`);

        // mark end of dragging
        dragData.current = dragInit;
        setSecondsPlayed(video.currentTime);
        setDragging(false);
        if (dragData.current.wasPlaying) {
          setPause(false);
        }
      }
    },
    [video]
  );

  const onPlaying = useCallback(() => {
    setPlaying(true);
  }, []);

  const onEnded = useCallback(() => {
    if (video) {
      setPause(true);
      setPlaying(false);
      setSecondsPlayed(duration);
    }
  }, [video, duration]);

  const onPause = useCallback(() => {
    setPlaying(false);
  }, []);

  const onCanPlayThrough = useCallback((e: SyntheticEvent<HTMLVideoElement, Event>) => {
    if (e.target instanceof HTMLVideoElement) {
      setDuration(e.target.duration);
      setCanPlay(true);
    }
  }, []);

  const togglePlay = useCallback(() => {
    // user wants to play video but it's basically ended, start it over
    if (pause && video && video.currentTime > video.duration - 1) {
      video.currentTime = 0;
      setSecondsPlayed(0);
    }

    setPause(!pause);
  }, [pause, video]);

  const toggleMute = useCallback(() => {
    setMute((prev) => !prev);
  }, []);

  useEffect(() => {
    const checkProgress = () => {
      setSecondsPlayed(video?.currentTime || 0);
    };

    if (playing && video) {
      tickRef.current = setInterval(checkProgress, 1000);
    }

    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
      }
    };
  }, [playing, video]);

  useEffect(() => {
    if (pause) {
      video?.pause();
    } else {
      video?.play().catch(console.error);
    }
  }, [pause, video]);

  useEffect(() => {
    if (!video) return;

    if (mute) {
      video.volume = 0;
    } else {
      video.volume = 1;
    }
  }, [video, mute]);

  useEffect(() => {
    window.addEventListener('pointermove', onMouseMove);
    window.addEventListener('pointerup', onMouseUp);

    return () => {
      window.removeEventListener('pointermove', onMouseMove);
      window.removeEventListener('pointerup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  useEffect(() => {
    setPause(true);
    setDuration(0);
    setSecondsPlayed(0);
  }, [videoUrl, video]);

  useEffect(() => {
    document.startViewTransition(() => {
      setUrl(videoUrl);
      setThumbnail(thumbnailUrl);
      parentRef.current?.style.setProperty('--bottom', isLower ? '60px' : '0px');
    });
  }, [videoUrl, thumbnailUrl, isLower]);

  useEffect(() => {
    if (!dragging) {
      setProgress((secondsPlayed / duration) * 100 + '%');
    }
  }, [secondsPlayed, duration, dragging]);

  return (
    <div
      ref={parentRef}
      className={styles.videoPlayerContainer}
      style={
        {
          '--progress': progress,
          '--cursor': dragging ? 'grabbing' : 'initial',
        } as CSSProperties
      }
    >
      <video
        ref={(ref) => setVideo(ref)}
        onCanPlay={onCanPlayThrough}
        onPlaying={onPlaying}
        onPause={onPause}
        onEnded={onEnded}
        className={styles.videoPlayer}
        src={url}
        poster={thumbnail}
      />
      <div className={styles.videoControls}>
        <div
          className={styles.videoTrackContainer}
          ref={trackRef}
          style={{ '--cursor': dragging ? 'grabbing' : 'pointer' } as CSSProperties}
        >
          <div className={styles.videoTrack} onPointerDown={onMouseDownTrack}>
            <div className={styles.videoPlayed} />
          </div>
          {duration >= 2 && (
            <div
              style={{ '--cursor': dragging ? 'grabbing' : 'grab' } as CSSProperties}
              className={styles.videoTrackThumb}
              onPointerDown={onMouseDownThumb}
              ref={thumbRef}
            />
          )}
        </div>
        <div className={styles.videoButtons}>
          <div className={styles.left}>
            <button onClick={togglePlay} className="no-outline" disabled={!canPlay}>
              {!pause ? <Icon name={ICONS.PAUSE_SHADOW} size={16} /> : <Icon name={ICONS.PLAY_SHADOW} size={16} />}
            </button>
            <button onClick={toggleMute} className={classNames('no-outline', styles.mute)}>
              {mute ? <Icon name={ICONS.MUTED_SHADOW} size={16} /> : <Icon name={ICONS.MUTE_SHADOW} size={16} />}
            </button>
            <span className={classNames('S2', styles.videoTime)}>
              <span>{formatMsToHMMSS(secondsPlayed * 1000)}</span>
              <span className="S1 margin-h-5">/</span>
              <span>{formatMsToHMMSS(duration * 1000)}</span>
            </span>
          </div>
          <div className={styles.right}>
            <button onClick={lastVideo} disabled={!canSelectLast}>
              <Icon name={ICONS.ANGLE_LEFT} size={16} />
            </button>
            <button onClick={nextVideo} disabled={!canSelectNext}>
              <Icon name={ICONS.ANGLE_RIGHT} size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
